<!--
  SPEC 44: the runs list. A run is one truck's ordered list of stops drawn
  from one or more loads. Every assignment already has one (a full-truckload
  load rides a one-load run nobody needs to see); this list is where a
  dispatcher sees them all for a date range and starts a new one to put
  several loads on one truck.

  Spec 49: this is the Runs tab of the Dispatch page (/dispatch?tab=runs),
  not a page of its own any more, so it draws no LwPageHeader: Dispatch owns
  the heading and the Create load / Import load buttons. Dispatch mounts it
  the first time the tab is shown and leaves it mounted, so the fetch below
  happens on first activation, not on every visit to the board. It reports
  its row total through `count` for the tab's badge, like the load lists.

  The list is GET /runs?from=&to=. Both dates are optional: left blank, the
  server answers its own default range (two days back to fourteen ahead) and
  says which range it used, which is what the line above the table prints.

  "New run" picks a driver, a truck and a trailer (a run needs a driver or a
  truck), POSTs /runs, and opens the new run's builder, which is where loads
  are added.
-->
<template>
  <div class="lw-p-runs">
    <p class="lw-p-runs__lede">
      Each truck's stops across its loads, in the order the truck runs them.
    </p>

    <div class="lw-p-runs__body">
      <LwPanel title="New run">
        <form
          class="lw-p-runs__new"
          novalidate
          data-test="new-run-form"
          @submit.prevent="createRun"
        >
          <div class="lw-p-runs__grid">
            <LwField
              v-model="newRun.driver_id"
              type="select"
              label="Driver"
              placeholder="No driver"
              :options="driverOptions"
              :disabled="creating"
              data-test="new-run-driver"
            />
            <LwField
              v-model="newRun.truck_id"
              type="select"
              label="Truck"
              placeholder="No truck"
              :options="truckOptions"
              :disabled="creating"
              data-test="new-run-truck"
            />
            <LwField
              v-model="newRun.trailer_id"
              type="select"
              label="Trailer"
              placeholder="No trailer"
              :options="trailerOptions"
              :disabled="creating"
              data-test="new-run-trailer"
            />
          </div>
          <LwNotice v-if="createErrors.length" tone="danger" data-test="new-run-error">
            <ul class="lw-p-runs__messages">
              <li v-for="(message, index) in createErrors" :key="index">{{ message }}</li>
            </ul>
          </LwNotice>
          <div class="lw-p-runs__actions">
            <LwButton
              type="submit"
              variant="primary"
              :loading="creating"
              loading-label="Creating run"
            >
              Create run
            </LwButton>
          </div>
        </form>
      </LwPanel>

      <LwPanel title="Runs">
        <template #actions>
          <form
            class="lw-p-runs__range"
            novalidate
            data-test="runs-range"
            @submit.prevent="fetchRuns"
          >
            <LwField v-model="from" type="date" label="From" data-test="runs-from" />
            <LwField v-model="to" type="date" label="To" data-test="runs-to" />
            <LwButton type="submit" variant="secondary">Show runs</LwButton>
          </form>
        </template>

        <p v-if="range" class="lw-p-runs__range-note" data-test="runs-range-note">
          Runs with stops from {{ range.from }} to {{ range.to }}.
        </p>

        <LwTable
          :columns="columns"
          :rows="runs"
          row-key="id"
          density="compact"
          :loading="loading"
          :error="loadError"
          caption="Runs"
          empty-title="No runs in this range"
          empty-body="A run appears here once a load is assigned to a driver or truck, or when you create one above."
          @retry="fetchRuns"
        >
          <template #cell-run="{ row }">
            <router-link
              class="lw-p-runs__link"
              :to="{ name: 'RunView', params: { id: String(row.id) } }"
              :data-test="`run-link-${row.id}`"
            >
              Run {{ row.id }}
            </router-link>
          </template>
          <template #cell-driver="{ row }">
            {{ row.driver ? row.driver.name : 'No driver' }}
          </template>
          <template #cell-equipment="{ row }">
            <span class="lw-p-runs__equipment">
              {{ row.truck ? row.truck.unit_number : 'No truck' }}
              <span v-if="row.trailer" class="lw-p-runs__meta">
                Trailer {{ row.trailer.unit_number }}
              </span>
            </span>
          </template>
          <template #cell-loads="{ row }">
            <ul class="lw-p-runs__loads">
              <li v-for="load in row.loads" :key="load.id" class="lw-p-runs__load">
                {{ load.customer_name || 'No customer' }}
                <span class="lw-p-runs__meta">{{ loadName(load) }}</span>
              </li>
            </ul>
          </template>
          <template #cell-stops="{ row }">
            <span class="lw-p-runs__figure">{{ row.stop_count }}</span>
          </template>
          <template #cell-first_stop="{ row }">
            {{ timeLabel(row.first_stop_at) }}
          </template>
          <template #cell-status="{ row }">
            <LwChip
              axis="feedback"
              :value="row.delivered ? 'success' : 'info'"
              :label="row.delivered ? 'Delivered' : 'Open'"
            />
          </template>
        </LwTable>
      </LwPanel>
    </div>
  </div>
</template>

<script>
import LwPanel from '../../components/lw/LwPanel.vue';
import LwField from '../../components/lw/LwField.vue';
import LwButton from '../../components/lw/LwButton.vue';
import LwNotice from '../../components/lw/LwNotice.vue';
import LwTable from '../../components/lw/LwTable.vue';
import LwChip from '../../components/lw/LwChip.vue';
import { loadLabel, refusalMessages, timeLabel } from './runFormat';

export default {
  name: 'Runs',
  components: { LwPanel, LwField, LwButton, LwNotice, LwTable, LwChip },
  emits: ['count'],
  data() {
    return {
      from: '',
      to: '',
      range: null,
      runs: [],
      loading: true,
      loadError: null,
      drivers: [],
      units: [],
      newRun: { driver_id: '', truck_id: '', trailer_id: '' },
      creating: false,
      createErrors: [],
      columns: [
        { key: 'run', label: 'Run', sortable: false },
        { key: 'driver', label: 'Driver', sortable: false },
        { key: 'equipment', label: 'Truck/Trailer', sortable: false },
        { key: 'loads', label: 'Loads', sortable: false },
        { key: 'stops', label: 'Stops', sortable: false },
        { key: 'first_stop', label: 'First stop', sortable: false },
        { key: 'status', label: 'Status', sortable: false },
      ],
    };
  },
  computed: {
    driverOptions() {
      return this.drivers.map(d => ({ value: d.id, label: d.name }));
    },
    truckOptions() {
      return this.units
        .filter(u => u.type === 'truck')
        .map(u => ({ value: u.id, label: u.unit_number }));
    },
    trailerOptions() {
      return this.units
        .filter(u => u.type === 'trailer')
        .map(u => ({ value: u.id, label: u.unit_number }));
    },
  },
  created() {
    this.fetchRuns();
    // The New run selects. A failed fetch leaves a select empty rather than
    // putting the page in an error state; the list still works.
    this.$http
      .get('/drivers')
      .then(res => {
        this.drivers = Array.isArray(res && res.data) ? res.data : [];
      })
      .catch(() => {});
    this.$http
      .get('/trucks/active')
      .then(res => {
        this.units = Array.isArray(res && res.data) ? res.data : [];
      })
      .catch(() => {});
  },
  methods: {
    timeLabel,
    loadName(load) {
      return loadLabel(load);
    },
    fetchRuns() {
      this.loading = true;
      this.loadError = null;
      const params = {};
      if (this.from) params.from = this.from;
      if (this.to) params.to = this.to;
      return this.$http
        .get('/runs', { params })
        .then(res => {
          const body = (res && res.data) || {};
          this.runs = Array.isArray(body.runs) ? body.runs : [];
          this.range = body.from && body.to ? { from: body.from, to: body.to } : null;
          this.$emit('count', this.runs.length);
        })
        .catch(err => {
          this.runs = [];
          this.range = null;
          this.loadError = refusalMessages(err && err.response, 'The runs could not be loaded.')[0];
          // A failed fetch reports null, and the tab's badge goes rather
          // than going stale.
          this.$emit('count', null);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    createRun() {
      if (this.creating) return undefined;
      this.createErrors = [];
      const body = {};
      ['driver_id', 'truck_id', 'trailer_id'].forEach(key => {
        if (this.newRun[key] !== '' && this.newRun[key] != null) body[key] = this.newRun[key];
      });
      if (!body.driver_id && !body.truck_id) {
        this.createErrors = ['Pick a driver or a truck. A run needs at least one of them.'];
        return undefined;
      }
      this.creating = true;
      return this.$http
        .post('/runs', body)
        .then(res => {
          this.creating = false;
          const id = res && res.data && res.data.id;
          if (this.$store && typeof this.$store.dispatch === 'function') {
            this.$store.dispatch('toasts/notify', { message: 'Run created.', tone: 'success' });
          }
          if (id != null) this.$router.push({ name: 'RunView', params: { id: String(id) } });
        })
        .catch(err => {
          this.creating = false;
          this.createErrors = refusalMessages(err && err.response, 'The run could not be created.');
        });
    },
  },
};
</script>

<style scoped lang="scss">
// No max-width here: the Dispatch page that hosts this tab already holds
// the content width (paper cut 20).
.lw-p-runs__lede {
  margin: 0 0 var(--lw-space-4);
  font: var(--lw-type-body);
  color: var(--lw-fg-secondary);
}

.lw-p-runs__body {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-5);
}

.lw-p-runs__new {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-4);
}

.lw-p-runs__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: var(--lw-space-4);
}

.lw-p-runs__actions {
  display: flex;
  justify-content: flex-end;
}

.lw-p-runs__range {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--lw-space-3);
}

.lw-p-runs__range-note {
  margin: 0 0 var(--lw-space-3);
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-p-runs__messages,
.lw-p-runs__loads {
  margin: 0;
  padding: 0;
  list-style: none;
}

.lw-p-runs__loads {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-1);
}

.lw-p-runs__link {
  font: var(--lw-type-body);
  font-weight: 600;
}

.lw-p-runs__equipment,
.lw-p-runs__load {
  display: flex;
  flex-direction: column;
}

.lw-p-runs__meta {
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-runs__figure {
  font: var(--lw-type-data-numeric);
}
</style>
